/*
 This file is part of GNU Taler
 (C) 2019 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

/**
 * Types and helper functions for dealing with Taler amounts.
 */

/**
 * Imports.
 */
import {
  Codec,
  Context,
  DecodingError,
  buildCodecForObject,
  renderContext,
} from "./codec.js";
import { Result, ResultError, ResultOk } from "./result.js";
import { AmountString, CurrencySpecification } from "./types-taler-common.js";

/**
 * Number of fractional units that one value unit represents.
 */
export const amountFractionalBase = 1e8;

/**
 * How many digits behind the comma are required to represent the
 * fractional value in human readable decimal format?  Must match
 * lg(fractionalBase)
 */
export const amountFractionalLength = 8;

/**
 * Maximum allowed value field of an amount.
 */
export const amountMaxValue = 2 ** 52;

/**
 * Separator character between integer and fractional
 */
export const FRAC_SEPARATOR = ".";

/**
 * Separator character between integer and fractional
 */
export const CURRENCY_SEPARATOR = ":";

/**
 * Non-negative financial amount.  Fractional values are expressed as multiples
 * of 1e-8.
 */
export interface AmountJson {
  /**
   * Value, must be an integer.
   */
  readonly value: number;

  /**
   * Fraction, must be an integer.  Represent 1/1e8 of a unit.
   */
  readonly fraction: number;

  /**
   * Currency of the amount.
   */
  readonly currency: string;
}

/**
 * Immutable amount.
 */
export class Amount {
  static from(a: AmountLike): Amount {
    return new Amount(Amounts.parseOrThrow(a), 0);
  }

  static zeroOfCurrency(currency: string): Amount {
    return new Amount(Amounts.zeroOfCurrency(currency), 0);
  }

  add(...a: AmountLike[]): Amount {
    const r = Amounts.add(this.val, ...a);
    return new Amount(r.amount, r.saturated ? 1 : 0);
  }

  isZero(): boolean {
    return this.val.fraction === 0 && this.val.value === 0;
  }

  sub(...a: AmountLike[]): Amount {
    const r = Amounts.sub(this.val, ...a);
    return new Amount(r.amount, r.saturated ? 1 : 0);
  }

  mult(n: number): Amount {
    const r = Amounts.mult(this, n);
    return new Amount(r.amount, r.saturated ? 1 : 0);
  }

  toJson(): AmountJson {
    return { ...this.val };
  }

  toString(): AmountString {
    return Amounts.stringify(this.val);
  }

  private constructor(
    private val: AmountJson,
    private saturated: number,
  ) {}
}

function codecForAmountValueNumber(): Codec<number> {
  return {
    decode(x: any, c?: Context): number {
      if (
        typeof x === "number" &&
        Number.isInteger(x) &&
        x >= 0 &&
        x <= amountMaxValue
      ) {
        return x;
      }
      throw new DecodingError(
        `expected valid amount value at ${renderContext(c)} but got ${JSON.stringify(x)}`,
      );
    },
  };
}

function codecForAmountFractionNumber(): Codec<number> {
  return {
    decode(x: any, c?: Context): number {
      if (
        typeof x === "number" &&
        Number.isInteger(x) &&
        x >= 0 &&
        x < amountFractionalBase
      ) {
        return x;
      }
      throw new DecodingError(
        `expected valid amount fraction at ${renderContext(c)} but got ${JSON.stringify(x)}`,
      );
    },
  };
}

/**
 * Codec for the currency of an amount, applying the same rule as
 * Amounts.isCurrency rather than accepting any string.
 */
export function codecForCurrency(): Codec<string> {
  return {
    decode(x: any, c?: Context): string {
      if (typeof x !== "string") {
        throw new DecodingError(
          `expected string at ${renderContext(c)} but got ${typeof x}`,
        );
      }
      const norm = Amounts.normalizeCurrency(x);
      if (norm === undefined) {
        throw new DecodingError(
          `expected valid currency at ${renderContext(
            c,
          )} but got ${JSON.stringify(x)}`,
        );
      }
      return norm;
    },
  };
}

export const codecForAmountJson = (): Codec<AmountJson> =>
  buildCodecForObject<AmountJson>()
    .property("currency", codecForCurrency())
    .property("value", codecForAmountValueNumber())
    .property("fraction", codecForAmountFractionNumber())
    .build("AmountJson");

export function codecForAmountString(): Codec<AmountString> {
  return {
    decode(x: any, c?: Context): AmountString {
      if (typeof x !== "string") {
        throw new DecodingError(
          `expected string at ${renderContext(c)} but got ${typeof x}`,
        );
      }
      if (Amounts.parse(x) === undefined) {
        throw new DecodingError(
          `invalid amount at ${renderContext(c)} got "${x}"`,
        );
      }
      return x as AmountString;
    },
  };
}

/**
 * Result of a possibly overflowing operation.
 */
export interface AmountResult {
  /**
   * Resulting, possibly saturated amount.
   */
  amount: AmountJson;
  /**
   * Was there an over-/underflow?
   */
  saturated: boolean;
}

/**
 * Type for things that are treated like amounts.
 */
export type AmountLike = string | AmountString | AmountJson | Amount;

export interface DivmodResult {
  quotient: number;
  remainder: AmountJson;
}

export enum AmountParseError {
  /**
   * Should have a string separated with a colon
   */
  MISSING_CURRENCY,
  /**
   * Currency should be less than 11 characters
   */
  CURRENCY_TOO_LONG,
  /**
   * Currency should be only letter from a to z
   */
  BAD_CURRENCY,
  /**
   * Number can only be digits from 0 to 9
   */
  BAD_NUMBER,
  /**
   * Integer part should be less than 2 ** 52
   */
  TOO_HIGH,
  /**
   * Fractional part should be shorter than 8 digits
   */
  TOO_PRECISE,
}

/**
 * Helper class for dealing with amounts.
 */
export class Amounts {
  private constructor() {
    throw Error("not instantiable");
  }

  static currencyOf(amount: AmountLike) {
    const amt = Amounts.parseOrThrow(amount);
    return amt.currency;
  }

  static zeroOfAmount(amount: AmountLike): AmountJson {
    const amt = Amounts.parseOrThrow(amount);
    return {
      currency: amt.currency,
      fraction: 0,
      value: 0,
    };
  }

  /**
   * Get an amount that represents zero units of a currency.
   */
  static zeroOfCurrency(currency: string): AmountJson {
    const c = Amounts.normalizeCurrency(currency);
    if (c === undefined) {
      throw Error(`not a currency: ${JSON.stringify(currency)}`);
    }
    return {
      currency: c,
      fraction: 0,
      value: 0,
    };
  }

  /**
   * Check the numeric fields of an amount, and return it unchanged.
   *
   * A denormalized fraction is accepted: add, cmp and stringify normalize
   * their operands.  The currency format is not re-checked, since every way
   * of constructing an amount already validates it via normalizeCurrency.
   */
  static checkAmountJson(amt: AmountJson): AmountJson {
    if (typeof amt !== "object" || amt === null) {
      throw Error("invalid amount (not an object)");
    }
    if (typeof amt.currency !== "string") {
      throw Error("invalid amount (currency is not a string)");
    }
    for (const field of ["value", "fraction"] as const) {
      const v = amt[field];
      if (typeof v !== "number" || !Number.isSafeInteger(v) || v < 0) {
        // JSON.stringify renders NaN and Infinity as "null".
        const shown = typeof v === "number" ? String(v) : JSON.stringify(v);
        throw Error(
          `invalid amount (${field} must be a non-negative safe integer, got ${shown})`,
        );
      }
    }
    return amt;
  }

  static jsonifyAmount(amt: AmountLike): AmountJson {
    if (typeof amt === "string") {
      return Amounts.parseOrThrow(amt);
    }
    if (amt instanceof Amount) {
      return amt.toJson();
    }
    return Amounts.checkAmountJson(amt);
  }

  static divmod(a1: AmountLike, a2: AmountLike): DivmodResult {
    const am1 = Amounts.jsonifyAmount(a1);
    const am2 = Amounts.jsonifyAmount(a2);
    if (am1.currency.toUpperCase() != am2.currency.toUpperCase()) {
      throw Error(`incompatible currency (${am1.currency} vs ${am2.currency})`);
    }

    const x1 =
      BigInt(am1.value) * BigInt(amountFractionalBase) + BigInt(am1.fraction);
    const x2 =
      BigInt(am2.value) * BigInt(amountFractionalBase) + BigInt(am2.fraction);

    const quotient = x1 / x2;
    const remainderScaled = x1 % x2;

    return {
      quotient: Number(quotient),
      remainder: {
        currency: am1.currency,
        value: Number(remainderScaled / BigInt(amountFractionalBase)),
        fraction: Number(remainderScaled % BigInt(amountFractionalBase)),
      },
    };
  }

  static sum(amounts: AmountLike[]): AmountResult {
    if (amounts.length <= 0) {
      throw Error("can't sum zero amounts");
    }
    const jsonAmounts = amounts.map((x) => Amounts.jsonifyAmount(x));
    return Amounts.add(jsonAmounts[0], ...jsonAmounts.slice(1));
  }

  static sumOrZero(currency: string, amounts: AmountLike[]): AmountResult {
    if (amounts.length <= 0) {
      return {
        amount: Amounts.zeroOfCurrency(currency),
        saturated: false,
      };
    }
    const jsonAmounts = amounts.map((x) => Amounts.jsonifyAmount(x));
    return Amounts.add(jsonAmounts[0], ...jsonAmounts.slice(1));
  }

  /**
   * Add two amounts.  Return the result and whether
   * the addition overflowed.  The overflow is always handled
   * by saturating and never by wrapping.
   *
   * Throws when currencies don't match.
   */
  static add(first: AmountLike, ...rest: AmountLike[]): AmountResult {
    const firstJ = Amounts.jsonifyAmount(first);
    const currency = firstJ.currency;

    // Validate all currencies before any numeric accumulation/overflow
    // check, so a mismatched currency in `rest` is always reported, even
    // if the first argument alone would already saturate the result.
    const restJ = rest.map((x) => Amounts.jsonifyAmount(x));
    for (const xJ of restJ) {
      if (xJ.currency.toUpperCase() !== currency.toUpperCase()) {
        throw Error(`Mismatched currency: ${xJ.currency} and ${currency}`);
      }
    }

    let value =
      firstJ.value + Math.floor(firstJ.fraction / amountFractionalBase);
    if (value > amountMaxValue) {
      return {
        amount: {
          currency,
          value: amountMaxValue,
          fraction: amountFractionalBase - 1,
        },
        saturated: true,
      };
    }
    let fraction = firstJ.fraction % amountFractionalBase;
    for (const xJ of restJ) {
      value =
        value +
        xJ.value +
        Math.floor((fraction + xJ.fraction) / amountFractionalBase);
      fraction = Math.floor((fraction + xJ.fraction) % amountFractionalBase);
      if (value > amountMaxValue) {
        return {
          amount: {
            currency,
            value: amountMaxValue,
            fraction: amountFractionalBase - 1,
          },
          saturated: true,
        };
      }
    }
    return { amount: { currency, value, fraction }, saturated: false };
  }

  /**
   * Subtract two amounts.  Return the result and whether
   * the subtraction overflowed.  The overflow is always handled
   * by saturating and never by wrapping.
   *
   * Throws when currencies don't match.
   */
  static sub(a: AmountLike, ...rest: AmountLike[]): AmountResult {
    const aJ = Amounts.jsonifyAmount(a);
    const currency = aJ.currency;
    // Normalize the minuend, as add() and cmp() do for their operands.
    let value = aJ.value + Math.floor(aJ.fraction / amountFractionalBase);
    let fraction = aJ.fraction % amountFractionalBase;

    // Validate every operand before subtracting any of them, as add() does.
    // Checking inside the loop made a mismatched currency invisible once an
    // earlier operand had already saturated the result.
    const restJ = rest.map((b) => Amounts.jsonifyAmount(b));
    for (const bJ of restJ) {
      if (bJ.currency.toUpperCase() !== aJ.currency.toUpperCase()) {
        throw Error(`Mismatched currency: ${bJ.currency} and ${currency}`);
      }
    }

    for (const bJ of restJ) {
      if (fraction < bJ.fraction) {
        if (value < 1) {
          return {
            amount: { currency, value: 0, fraction: 0 },
            saturated: true,
          };
        }
        value--;
        fraction += amountFractionalBase;
      }
      if (fraction < bJ.fraction) {
        throw Error("assertion failed: fraction underflow in Amounts.sub");
      }
      fraction -= bJ.fraction;
      if (value < bJ.value) {
        return { amount: { currency, value: 0, fraction: 0 }, saturated: true };
      }
      value -= bJ.value;
    }

    return { amount: { currency, value, fraction }, saturated: false };
  }

  /**
   * Compare two amounts.  Returns 0 when equal, -1 when a < b
   * and +1 when a > b.  Throws when currencies don't match.
   */
  static cmp(a: AmountLike, b: AmountLike): -1 | 0 | 1 {
    a = Amounts.jsonifyAmount(a);
    b = Amounts.jsonifyAmount(b);
    if (a.currency.toUpperCase() !== b.currency.toUpperCase()) {
      throw Error(`Mismatched currency: ${a.currency} and ${b.currency}`);
    }
    const av = a.value + Math.floor(a.fraction / amountFractionalBase);
    const af = a.fraction % amountFractionalBase;
    const bv = b.value + Math.floor(b.fraction / amountFractionalBase);
    const bf = b.fraction % amountFractionalBase;
    // jsonifyAmount has established that all four are non-negative safe
    // integers, so these comparisons are total.
    if (av < bv) return -1;
    if (av > bv) return 1;
    if (af < bf) return -1;
    if (af > bf) return 1;
    return 0;
  }

  /**
   * Create a copy of an amount.
   */
  static copy(a: AmountJson): AmountJson {
    return {
      currency: a.currency,
      fraction: a.fraction,
      value: a.value,
    };
  }

  /**
   * Divide an amount.  Throws on division by zero.
   */
  static divide(a: AmountLike, n: number): AmountJson {
    if (!Number.isInteger(n) || n <= 0) {
      throw Error(`Amount can only be divided by a positive integer`);
    }
    const aJ = Amounts.jsonifyAmount(a);
    // Normalize the dividend before dividing, as the C reference does; an
    // out-of-range fraction would otherwise survive into the quotient.
    const value = aJ.value + Math.floor(aJ.fraction / amountFractionalBase);
    const fraction = aJ.fraction % amountFractionalBase;
    if (n === 1) {
      return { value, fraction, currency: aJ.currency };
    }
    const r = value % n;
    // Scale in BigInt: r can be as large as n-1, so r * amountFractionalBase
    // exceeds 2^53 and float64 would round the fraction's low bits away.
    const scaled =
      (BigInt(r) * BigInt(amountFractionalBase) + BigInt(fraction)) / BigInt(n);
    return {
      currency: aJ.currency,
      fraction: Number(scaled),
      value: Math.floor(value / n),
    };
  }

  /**
   * Check if an amount is non-zero.
   */
  static isNonZero(a: AmountLike): boolean {
    a = Amounts.jsonifyAmount(a);
    return a.value > 0 || a.fraction > 0;
  }

  static isZero(a: AmountLike): boolean {
    a = Amounts.jsonifyAmount(a);
    return a.value === 0 && a.fraction === 0;
  }

  /**
   * Check whether a string is a valid currency for a Taler amount:
   * at most 11 ASCII letters.
   */
  static isCurrency(s: string): boolean {
    return /^[a-zA-Z]{1,11}$/.test(s);
  }

  /**
   * Validate a currency and return it in canonical (upper case) form,
   * or undefined if it is not valid.
   *
   * Validation must run on the raw string: toUpperCase is neither ASCII-only
   * nor length-preserving (U+017F folds to "S", U+FB01 to "FI").
   */
  static normalizeCurrency(s: string): string | undefined {
    if (!Amounts.isCurrency(s)) {
      return undefined;
    }
    return s.toUpperCase();
  }

  /**
   * Parse an amount like 'EUR:20.5' for 20 Euros and 50 ct.
   *
   * Currency name size limit is 11 of ASCII letters
   * Fraction size limit is 8
   */
  static parseWithError(s: string):
    | ResultOk<{
        currency: string;
        fraction: number;
        value: number;
      }>
    | ResultError<AmountParseError, undefined> {
    const c_idx = s.indexOf(CURRENCY_SEPARATOR);

    if (c_idx === -1 || c_idx === 0) {
      return Result.error(AmountParseError.MISSING_CURRENCY);
    }
    const rawCurrency = s.substring(0, c_idx);
    if (rawCurrency.length > 11) {
      return Result.error(AmountParseError.CURRENCY_TOO_LONG);
    }
    const currency = Amounts.normalizeCurrency(rawCurrency);
    if (currency === undefined) {
      return Result.error(AmountParseError.BAD_CURRENCY);
    }
    const number = s.substring(c_idx + 1);
    const d_idx = number.indexOf(FRAC_SEPARATOR);
    const integerStr = d_idx === -1 ? number : number.substring(0, d_idx);
    const fractStr =
      d_idx === -1 || d_idx === number.length
        ? "0"
        : number.substring(d_idx + 1);

    if (!/^[0-9]+$/.test(integerStr) || !/^[0-9]+$/.test(fractStr)) {
      return Result.error(AmountParseError.BAD_NUMBER);
    }

    const value = Number.parseInt(integerStr, 10);
    const fraction = Math.round(
      amountFractionalBase * Number.parseFloat(FRAC_SEPARATOR + fractStr),
    );
    if (!Number.isInteger(value) || !Number.isInteger(fraction)) {
      return Result.error(AmountParseError.BAD_NUMBER);
    }
    if (value > amountMaxValue) {
      return Result.error(AmountParseError.TOO_HIGH);
    }
    if (fractStr.length > amountFractionalLength) {
      return Result.error(AmountParseError.TOO_PRECISE);
    }
    return Result.of({
      currency,
      fraction,
      value,
    });
  }

  /**
   * Parse an amount like 'EUR:20.5' for 20 Euros and 50 ct.
   *
   * Currency name size limit is 11 of ASCII letters
   * Fraction size limit is 8
   */
  static parse(s: string): AmountJson | undefined {
    const res = s.match(/^([a-zA-Z]{1,11}):([0-9]+)([.][0-9]{1,8})?$/);
    if (!res) {
      return undefined;
    }
    const tail = res[3] || FRAC_SEPARATOR + "0";
    if (tail.length > amountFractionalLength + 1) {
      return undefined;
    }
    const value = Number.parseInt(res[2]);
    if (value > amountMaxValue) {
      return undefined;
    }
    const currency = Amounts.normalizeCurrency(res[1]);
    if (currency === undefined) {
      return undefined;
    }
    return {
      currency,
      fraction: Math.round(amountFractionalBase * Number.parseFloat(tail)),
      value,
    };
  }

  /**
   * Parse amount in standard string form (like 'EUR:20.5'),
   * throw if the input is not a valid amount.
   */
  static parseOrThrow(s: AmountLike): AmountJson {
    if (s instanceof Amount) {
      return s.toJson();
    }
    if (typeof s === "object") {
      if (typeof s.currency !== "string") {
        throw Error("invalid amount object");
      }
      const currency = Amounts.normalizeCurrency(s.currency);
      if (currency === undefined) {
        throw Error(
          `invalid amount object (not a currency: ${JSON.stringify(
            s.currency,
          )})`,
        );
      }
      if (typeof s.value !== "number") {
        throw Error("invalid amount object");
      }
      if (typeof s.fraction !== "number") {
        throw Error("invalid amount object");
      }
      if (
        !Number.isInteger(s.value) ||
        s.value < 0 ||
        s.value > amountMaxValue
      ) {
        throw Error("invalid amount object (value out of range)");
      }
      if (
        !Number.isInteger(s.fraction) ||
        s.fraction < 0 ||
        s.fraction >= amountFractionalBase
      ) {
        throw Error("invalid amount object (fraction out of range)");
      }
      return { currency, value: s.value, fraction: s.fraction };
    } else if (typeof s === "string") {
      const res = Amounts.parse(s);
      if (!res) {
        throw Error(`Can't parse amount: "${s}"`);
      }
      return res;
    } else {
      throw Error("invalid amount (illegal type)");
    }
  }

  static min(a: AmountLike, b: AmountLike): AmountJson {
    const cr = Amounts.cmp(a, b);
    if (cr >= 0) {
      return Amounts.jsonifyAmount(b);
    } else {
      return Amounts.jsonifyAmount(a);
    }
  }

  static max(a: AmountLike, b: AmountLike): AmountJson {
    const cr = Amounts.cmp(a, b);
    if (cr >= 0) {
      return Amounts.jsonifyAmount(a);
    } else {
      return Amounts.jsonifyAmount(b);
    }
  }

  static mult(a: AmountLike, n: number): AmountResult {
    a = this.jsonifyAmount(a);
    if (!Number.isInteger(n)) {
      throw Error("amount can only be multiplied by an integer");
    }
    if (n < 0) {
      throw Error("amount can only be multiplied by a positive integer");
    }
    if (n == 0) {
      return {
        amount: Amounts.zeroOfCurrency(a.currency),
        saturated: false,
      };
    }
    let x = a;
    let acc = Amounts.zeroOfCurrency(a.currency);
    while (n > 1) {
      if (n % 2 == 0) {
        n = n / 2;
      } else {
        n = (n - 1) / 2;
        const r2 = Amounts.add(acc, x);
        if (r2.saturated) {
          return r2;
        }
        acc = r2.amount;
      }
      const r2 = Amounts.add(x, x);
      if (r2.saturated) {
        return r2;
      }
      x = r2.amount;
    }
    return Amounts.add(acc, x);
  }

  /**
   * Check if the argument is a valid amount in string form.
   */
  static checkString(s: string): s is AmountString {
    try {
      const parsedAmount = Amounts.parse(s);
      return !!parsedAmount;
    } catch {
      return false;
    }
  }

  /**
   * Convert to standard human-readable string representation that's
   * also used in JSON formats.
   */
  static stringify(a: AmountLike): AmountString {
    a = Amounts.jsonifyAmount(a);
    const s = this.stringifyValue(a);

    return `${a.currency}:${s}` as AmountString;
  }

  /**
   * Show an amount in a form suitable for the user.
   * FIXME:  In the future, this should consider currency-specific
   * settings such as significant digits or currency symbols.
   */
  static toPretty(amount: AmountJson): string {
    const x = amount.value + amount.fraction / amountFractionalBase;
    return `${x} ${amount.currency}`;
  }

  /**
   * Render an amount for a user to read.
   *
   * Formats parsed amounts as `CURRENCY VALUE` with at least 2 fractional digits
   * (e.g., `CHF 12.50`).
   *
   * If amount is undefined, null, or empty string, returns "—".
   * Unparseable strings are returned unchanged.
   */
  static formatAmount(amount: AmountLike | undefined | null): string {
    if (!amount) return "—";
    if (typeof amount === "string") {
      const parsed = Amounts.parse(amount);
      if (!parsed) return amount;
      return `${parsed.currency} ${Amounts.stringifyValue(parsed, 2)}`;
    }
    try {
      const parsed = Amounts.jsonifyAmount(amount);
      return `${parsed.currency} ${Amounts.stringifyValue(parsed, 2)}`;
    } catch {
      return String(amount);
    }
  }

  /**
   * The currency of an amount, or the empty string if it has none or is invalid.
   */
  static amountCurrency(amount: AmountLike | undefined | null): string {
    if (!amount) return "";
    if (typeof amount === "string") {
      return Amounts.parse(amount)?.currency ?? "";
    }
    try {
      return Amounts.jsonifyAmount(amount).currency;
    } catch {
      return "";
    }
  }

  static isSameCurrency(curr1: string, curr2: string): boolean {
    return curr1.toLowerCase() === curr2.toLowerCase();
  }

  /**
   * Stringify only the value part of an amount,
   * leaving out the currency name.
   */
  static stringifyValue(a: AmountLike, minFractional = 0): string {
    const aJ = Amounts.jsonifyAmount(a);
    const av = aJ.value + Math.floor(aJ.fraction / amountFractionalBase);
    const af = aJ.fraction % amountFractionalBase;
    let s = av.toString();

    if (af || minFractional) {
      s = s + FRAC_SEPARATOR;
      let n = af;
      for (let i = 0; i < amountFractionalLength; i++) {
        if (!n && i >= minFractional) {
          break;
        }
        s = s + Math.floor((n / amountFractionalBase) * 10).toString();
        n = (n * 10) % amountFractionalBase;
      }
    }

    return s;
  }

  /**
   * Number of fractional digits needed to fully represent the amount
   * @param a amount
   * @returns
   */
  static maxFractionalDigits(a: AmountJson): number {
    if (a.fraction === 0) return 0;
    if (a.fraction < 0) {
      console.error("amount fraction can not be negative", a);
      return 0;
    }
    let i = 0;
    let check = true;
    let rest = a.fraction;
    while (rest > 0 && check) {
      check = rest % 10 === 0;
      rest = rest / 10;
      i++;
    }
    return amountFractionalLength - i + 1;
  }

  static stringifyValueWithSpec(
    value: AmountJson,
    spec: CurrencySpecification | undefined,
  ): { currency: string; normal: string; small?: string } {
    const strValue = Amounts.stringifyValue(value);
    let currency = value.currency;
    if (!spec) {
      return {
        currency,
        normal: strValue,
      };
    }
    const pos = strValue.indexOf(FRAC_SEPARATOR);
    const originalPosition = pos < 0 ? strValue.length : pos;

    const names = !spec ? [] : Object.keys(spec.alt_unit_names);
    let FRAC_POS_NEW_POSITION = originalPosition;
    //find symbol
    //FIXME: this should be based on a cache to speed up
    if (spec && names.length > 0) {
      let unitIndex: string = "0"; //default entry by DD51
      names.forEach((index) => {
        const i = Number.parseInt(index, 10);
        if (Number.isNaN(i)) return; //skip
        if (originalPosition - i <= 0) return; //too big
        if (originalPosition - i < FRAC_POS_NEW_POSITION) {
          FRAC_POS_NEW_POSITION = originalPosition - i;
          unitIndex = index;
        }
      });
      currency = spec.alt_unit_names[unitIndex];
    }

    if (originalPosition === FRAC_POS_NEW_POSITION) {
      const { normal, small } = splitNormalAndSmall(
        strValue,
        originalPosition,
        spec,
      );
      return { currency, normal, small };
    }

    const intPart = strValue.substring(0, originalPosition);
    const fracPArt = strValue.substring(originalPosition + 1);
    //indexSize is always smaller than originalPosition
    const newValue =
      intPart.substring(0, FRAC_POS_NEW_POSITION) +
      FRAC_SEPARATOR +
      intPart.substring(FRAC_POS_NEW_POSITION) +
      fracPArt;
    const { normal, small } = splitNormalAndSmall(
      newValue,
      FRAC_POS_NEW_POSITION,
      spec,
    );
    return { currency, normal, small };
  }
}

function splitNormalAndSmall(
  decimal: string,
  fracSeparatorIndex: number,
  spec: CurrencySpecification,
): { normal: string; small?: string } {
  let normal: string;
  let small: string | undefined;
  if (
    decimal.length - fracSeparatorIndex - 1 >
    spec.num_fractional_normal_digits
  ) {
    const limit = fracSeparatorIndex + spec.num_fractional_normal_digits + 1;
    normal = decimal.substring(0, limit);
    small = decimal.substring(limit);
  } else {
    normal = decimal;
    small = undefined;
  }
  return { normal, small };
}
